import { useState, useEffect } from 'react';

interface ShareSheetProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productPrice: number;
  productCategory: string;
  productImage?: string;
  productId?: number | string;
}

interface ShareOption {
  id: string;
  label: string;
  icon: string;
  bg: string;
  iconColor: string;
}

const shareOptions: ShareOption[] = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', bg: 'bg-green-500', iconColor: 'text-white' },
  { id: 'facebook', label: 'Facebook', icon: 'ri-facebook-fill', bg: 'bg-[#1877f2]', iconColor: 'text-white' },
  { id: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400', iconColor: 'text-white' },
  { id: 'twitter', label: 'X (Twitter)', icon: 'ri-twitter-x-line', bg: 'bg-black', iconColor: 'text-white' },
  { id: 'telegram', label: 'Telegram', icon: 'ri-telegram-fill', bg: 'bg-[#2AABEE]', iconColor: 'text-white' },
  { id: 'copy', label: 'Copy Link', icon: 'ri-links-line', bg: 'bg-gray-200', iconColor: 'text-gray-700' },
];

export default function ShareSheet({
  isOpen, onClose, productName, productPrice, productCategory, productImage, productId
}: ShareSheetProps) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setVisible(true), 10);
      document.body.style.overflow = 'hidden';
    } else {
      setVisible(false);
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const shareUrl = productId 
    ? `${window.location.origin}/category?cat=${encodeURIComponent(productCategory)}&product=${encodeURIComponent(productId)}`
    : `${window.location.origin}/category?cat=${encodeURIComponent(productCategory)}`;
  const shareText = `Check this out: ${productName} at just ₹${productPrice}! 🔥 Shop on ClothyBazar`;

  const handleShare = (platform: string) => {
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl  = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, '_blank');
        break;
      case 'instagram': {
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
        window.open('https://www.instagram.com/', '_blank');
        break;
      }
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank');
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(`${shareText} ${shareUrl}`).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2500);
        });
        break;
      default:
        break;
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: productName, text: shareText, url: shareUrl });
      } catch {
        // user cancelled
      }
    }
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={handleClose}
      />

      {/* Sheet */}
      <div
        className="relative w-full max-w-lg bg-white rounded-t-3xl overflow-hidden shadow-2xl transition-transform duration-300"
        style={{ transform: visible ? 'translateY(0)' : 'translateY(100%)' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200"></div>
        </div>

        <div className="px-5 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-5 pt-2">
            <h3 className="text-lg font-bold text-gray-900">Share Product</h3>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>
          </div>

          {/* Product Preview */}
          <div className="flex items-center gap-3 p-3 rounded-2xl bg-gray-50 border border-gray-100 mb-6">
            {productImage && (
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-white border border-gray-100">
                <img src={productImage} alt={productName} className="w-full h-full object-contain" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-semibold text-sm leading-tight truncate">{productName}</p>
              <p className="text-gray-500 text-xs mt-0.5">{productCategory}</p>
              <p className="text-transparent bg-clip-text font-bold text-sm mt-0.5"
                 style={{ backgroundImage: 'linear-gradient(90deg,#a855f7,#ec4899)' }}>
                ₹{productPrice}
              </p>
            </div>
          </div>

          {/* Share Options Grid */}
          <div className="grid grid-cols-6 gap-3 mb-5">
            {shareOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleShare(opt.id)}
                className="flex flex-col items-center gap-1.5 cursor-pointer group"
              >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${opt.bg} group-hover:scale-110 transition-transform shadow-sm`}>
                  <i className={`${opt.icon} ${opt.iconColor} text-xl`}></i>
                </div>
                <span className="text-gray-600 text-[10px] font-medium leading-tight text-center whitespace-nowrap">
                  {opt.id === 'copy' && copied ? 'Copied!' : opt.label}
                </span>
              </button>
            ))}
          </div>

          {/* Instagram Note */}
          <div className="flex items-start gap-2.5 bg-gradient-to-r from-purple-50 to-pink-50 border border-pink-100 rounded-2xl px-4 py-3 mb-4">
            <i className="ri-instagram-line text-pink-500 text-lg shrink-0 mt-0.5"></i>
            <p className="text-gray-600 text-xs leading-relaxed">
              <span className="font-semibold text-gray-800">Instagram:</span> Link is copied to clipboard! Open Instagram → Create Story or Post → Paste the link in bio or DM.
            </p>
          </div>

          {/* Web Share API button — shows if supported */}
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button
              onClick={handleNativeShare}
              className="w-full py-3.5 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 text-sm font-medium flex items-center justify-center gap-2 hover:border-gray-300 hover:text-gray-700 transition-all cursor-pointer"
            >
              <i className="ri-share-forward-2-line text-lg"></i>
              More options…
            </button>
          )}

          {/* Copied Toast */}
          {copied && (
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] bg-gray-900 text-white text-sm font-semibold px-5 py-3 rounded-full flex items-center gap-2 shadow-xl">
              <i className="ri-check-double-line text-green-400"></i>
              Link Copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
